import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { PageHeader, KPI, Card, DataTable } from "@/components/Primitives";
import StatusPill from "@/components/StatusPill";
import { useNavigate } from "react-router-dom";

export default function Dashboard() {
  const [m, setM] = useState(null);
  const [recent, setRecent] = useState({ opps: [], cands: [] });
  const nav = useNavigate();

  useEffect(() => {
    api.get("/dashboard/metrics").then((r) => setM(r.data)).catch(() => {});
    Promise.all([
      api.get("/collections/opportunities"),
      api.get("/collections/candidate_opportunities"),
    ]).then(([a, b]) => setRecent({ opps: a.data.slice(0, 6), cands: b.data.slice(0, 6) })).catch(() => {});
  }, []);

  return (
    <div>
      <PageHeader
        overline="Operations"
        title="Command Dashboard"
        sub="Real-time intelligence across discovery, qualification, suppliers and production."
      />
      <div className="p-8 space-y-8">
        {!m ? <div className="text-zinc-500 text-xs tracking-widest uppercase">Loading metrics&hellip;</div> : (
          <>
            <section className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <KPI label="Active Opportunities" value={m.active_opportunities} hint={`${m.total_opportunities} total`} />
              <KPI label="High-Fit Opportunities" value={m.high_fit_opportunities} accent hint="fit_score ≥ 70" />
              <KPI label="Candidate Review Queue" value={m.candidate_review_count} hint={`${m.high_fit_candidates} high-fit`} />
              <KPI label="Open Tasks" value={m.open_tasks} hint="across all assignees" />
            </section>

            <section className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <KPI label="Deadlines · 14d" value={m.deadlines_14} />
              <KPI label="Deadlines · 30d" value={m.deadlines_30} />
              <KPI label="Deadlines · 60d" value={m.deadlines_60} />
              <KPI label="Deadlines · 90d" value={m.deadlines_90} />
            </section>

            <section className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <KPI label="Samples Due Soon" value={m.samples_due_soon} />
              <KPI label="Deliverables Due Soon" value={m.deliverables_due_soon} />
              <KPI label="Delayed Production" value={m.delayed_production} />
              <KPI label="Active Search Rules" value={m.active_search_rules} hint={`${m.total_search_rules} total`} />
            </section>

            <section className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <Card className="p-4">
                <div className="text-[10px] tracking-[0.25em] uppercase text-zinc-500 mb-2">Estimated Pipeline Value</div>
                <div className="space-y-2">
                  {Object.entries(m.pipeline_value_by_currency || {}).length === 0 && (
                    <div className="text-zinc-600 text-xs">No valued opportunities yet</div>
                  )}
                  {Object.entries(m.pipeline_value_by_currency || {}).map(([cur, val]) => (
                    <div key={cur} className="flex justify-between items-center border-b border-white/5 pb-2">
                      <span className="font-mono-data text-[11px] text-zinc-400">{cur}</span>
                      <span className="font-heading font-bold text-xl text-[#FFB300] tabular">
                        {Intl.NumberFormat(undefined, { maximumFractionDigits: 0 }).format(val)}
                      </span>
                    </div>
                  ))}
                </div>
                <div className="mt-4 grid grid-cols-2 gap-3 text-xs">
                  <div>
                    <div className="text-zinc-500 text-[10px] uppercase tracking-widest mb-1">Avg Fit Score</div>
                    <div className="font-heading font-bold text-2xl text-white">{m.avg_fit_score ?? "—"}</div>
                  </div>
                  <div>
                    <div className="text-zinc-500 text-[10px] uppercase tracking-widest mb-1">Avg Candidate Score</div>
                    <div className="font-heading font-bold text-2xl text-white">{m.avg_candidate_score ?? "—"}</div>
                  </div>
                </div>
              </Card>

              <Card className="p-4">
                <div className="text-[10px] tracking-[0.25em] uppercase text-zinc-500 mb-2">Coverage</div>
                <div className="grid grid-cols-2 gap-4 text-xs">
                  <div>
                    <div className="text-zinc-400 text-[10px] uppercase tracking-widest mb-2">By Product Category</div>
                    {Object.entries(m.product_category_coverage || {}).map(([k, v]) => (
                      <div key={k} className="flex justify-between border-b border-white/5 py-1">
                        <span className="text-zinc-300">{k}</span>
                        <span className="font-mono-data text-zinc-500">{v}</span>
                      </div>
                    ))}
                  </div>
                  <div>
                    <div className="text-zinc-400 text-[10px] uppercase tracking-widest mb-2">By Division</div>
                    {Object.entries(m.division_coverage || {}).map(([k, v]) => (
                      <div key={k} className="flex justify-between border-b border-white/5 py-1">
                        <span className="text-zinc-300">{k}</span>
                        <span className="font-mono-data text-zinc-500">{v}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </Card>
            </section>

            <section className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <div>
                <div className="flex items-center justify-between mb-2">
                  <div className="text-[10px] tracking-[0.25em] uppercase text-zinc-500">Recent Opportunities</div>
                  <button onClick={() => nav("/opportunities")} className="text-[10px] text-[#FFB300] tracking-widest uppercase hover:underline">View all →</button>
                </div>
                <DataTable
                  testId="recent-opps-table"
                  onRowClick={(r) => nav(`/opportunities/${r.id}`)}
                  columns={[
                    { key: "opportunity_name", label: "Name", render: (r) => <span className="text-white">{r.opportunity_name}</span> },
                    { key: "country", label: "Country" },
                    { key: "fit_score", label: "Fit", mono: true },
                    { key: "deadline", label: "Deadline", mono: true },
                    { key: "status", label: "Status", render: (r) => <StatusPill value={r.status} /> },
                  ]}
                  rows={recent.opps}
                />
              </div>
              <div>
                <div className="flex items-center justify-between mb-2">
                  <div className="text-[10px] tracking-[0.25em] uppercase text-zinc-500">Recent Candidates</div>
                  <button onClick={() => nav("/candidates")} className="text-[10px] text-[#FFB300] tracking-widest uppercase hover:underline">Review queue →</button>
                </div>
                <DataTable
                  testId="recent-cands-table"
                  onRowClick={(r) => nav(`/candidates/${r.id}`)}
                  columns={[
                    { key: "candidate_name", label: "Name", render: (r) => <span className="text-white">{r.candidate_name}</span> },
                    { key: "country", label: "Country" },
                    { key: "suggested_fit_score", label: "Score", mono: true },
                    { key: "duplicate_check", label: "Dup", render: (r) => <StatusPill value={r.duplicate_check} /> },
                    { key: "review_status", label: "Status", render: (r) => <StatusPill value={r.review_status} /> },
                  ]}
                  rows={recent.cands}
                />
              </div>
            </section>

            <section>
              <div className="text-[10px] tracking-[0.25em] uppercase text-zinc-500 mb-2">Recent Search Runs</div>
              <DataTable
                columns={[
                  { key: "search_run_name", label: "Run" },
                  { key: "run_date", label: "Date", mono: true },
                  { key: "result_count", label: "Results", mono: true },
                  { key: "candidates_added", label: "Candidates", mono: true },
                  { key: "duplicates_found", label: "Duplicates", mono: true },
                  { key: "run_status", label: "Status", render: (r) => <StatusPill value={r.run_status} /> },
                ]}
                rows={m.recent_search_runs || []}
              />
            </section>
          </>
        )}
      </div>
    </div>
  );
}
